import {
  FieldLabel,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import Box from 'src/components/Box/Box'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import CurrencyDropdown from 'src/components/CurrencyDropdown/CurrencyDropdown'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SvgUpdate from 'src/components/Svgs/SvgUpdate'
import { ExtendCurrency } from 'src/enums'
import { useFormFields } from 'src/hooks'
import Trns from 'src/localization/Trns'
import { UserListModel } from 'src/models/user.models'
import {
  PATCH_USER_CURRENCY_INIT,
  PatchUserActions,
} from 'src/redux/actions/data/users.actions'
import { selectIsCspAuthenticatedFromState } from 'src/redux/selectors/auth.selectors'
import { PatchUserRequest } from 'src/requests/patchUser.request'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { css } from 'styled-components'
import worldCurrencies from 'world-currencies'

interface CurrencyInfoBoxProps {
  className?: string
  user: UserListModel
  patchingUser: boolean
  patchingUserCurrency: boolean
  errorDetailMap?: ErrorDetailMap<string>
  patchUser: (
    body: Partial<PatchUserRequest>,
    patchType: PatchUserActions,
  ) => void
}

export type CurrrencyInfoFlatFields = {
  currency?: ExtendCurrency
}

const CurrencyInfoBox: React.FC<CurrencyInfoBoxProps> = (
  props,
): React.ReactElement => {
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const isCspAuthenticated = useSelector(selectIsCspAuthenticatedFromState)

  const {
    fields: flatFields,
    setFields: setFlatFields,
    fieldsErrors: flatFieldsErrors,
    onFieldChange: onFlatFieldChange,
    setErrorsAndReturnDidPassValidation: flatFieldsSetErrors,
  } = useFormFields<CurrrencyInfoFlatFields>( // TODO: should make this string | undefined to be totally transparent
    {
      currency: props.user.currency,
    },
    () => ({}),
    props.errorDetailMap,
  )
  useEffect((): void => {
    setFlatFields({
      currency: props.user.currency,
    })
  }, [props.user])

  const onSuccessfulSubmit = (flatFields: CurrrencyInfoFlatFields): void => {
    props.patchUser(
      {
        currency: flatFields.currency ? flatFields.currency : undefined,
      },
      {
        type: PATCH_USER_CURRENCY_INIT,
      },
    )
  }

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()

    const flatFieldsPassedValidation = flatFieldsSetErrors()

    if (flatFieldsPassedValidation) {
      onSuccessfulSubmit(flatFields)
    }
  }

  useEffect((): void => {
    if (props.patchingUserCurrency === false) {
      setIsEditing(false)
    }
  }, [props.patchingUserCurrency])

  return (
    <Box
      className={props.className}
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <span
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          ${getFontCss(
            20,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
        `}
      >
        <Trns source={'Default Currency'} />
        {!isEditing && (
          <HoverableLink
            onClick={(): void => {
              setIsEditing(true)
            }}
            ariaLabelSource={'Update'}
            css={css`
              display: flex;
              align-items: center;
            `}
            colorSvgOnHover
            disabled={isCspAuthenticated}
          >
            <Trns source={'Update'} />
            <SvgUpdate
              className={'svg-update'}
              css={css`
                transform: scale(0.5);
              `}
            />
          </HoverableLink>
        )}
      </span>
      <span
        css={css`
          margin-top: 30px;
        `}
      >
        <Trns
          source={
            'Choose the default currency for requesting budgets and virtual cards.'
          }
        />
      </span>
      <form
        onSubmit={onSubmit}
        css={css`
          margin-top: 30px;
          display: flex;
          flex-direction: column;
          .field-label {
            overflow: visible;
          }
        `}
      >
        <FieldLabel className={'currency-label'}>
          <Trns source={'Currency'} />
        </FieldLabel>
        {!isEditing && (
          <span
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )}
            `}
          >
            <Trns
              source={
                worldCurrencies[props.user.currency].name + ' (${isoCode})'
              }
              expressions={{
                isoCode: props.user.currency,
              }}
            />
          </span>
        )}
        {isEditing && (
          <span>
            <CurrencyDropdown
              className={'currency-input'}
              selectedValue={flatFields.currency}
              chooseOption={(val: string): void =>
                onFlatFieldChange('currency', val as ExtendCurrency)
              }
              errors={flatFieldsErrors.currency}
            />
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 40px;
                margin-top: 40px;
              `}
            >
              <Button
                design={ButtonDesigns.designCta}
                type={ButtonTypes.submit}
                text={<Trns source={'Save as Default Currency'} />}
                loading={props.patchingUserCurrency}
                disabled={props.patchingUser}
                big
              />
              <HoverableLink
                onClick={(): void => {
                  setIsEditing(false)
                }}
                underline
                ariaLabelSource={'Cancel'}
              >
                <Trns source={'Cancel'} />
              </HoverableLink>
            </div>
          </span>
        )}
      </form>
    </Box>
  )
}

export default CurrencyInfoBox
